/**
 * The payment table (R6b), over R6a rows.
 *
 * NOTHING HERE IS DERIVED. The order of the rows, how many there are and how
 * many exist in total are all the server's answers; this component reports a
 * header click and an offset change upward and paints whatever comes back.
 *
 * FOUR PLACES A PLAUSIBLE VALUE COULD BE INVENTED, AND ARE NOT:
 *
 *   THE CURRENCY. A payment row has no currency column; it inherits the resolved
 *   invoice's, and an orphan has no invoice at all. NULL is a third state — not
 *   CHF, not foreign, but "nobody told us" — so the amount goes through
 *   `fmtInvoiceMoney`, which carries that distinction, rather than a franc
 *   formatter.
 *
 *   THE DATE. `synced_at` records when WE copied the row, not when money moved.
 *   A row without a payment date says so and stops.
 *
 *   THE POPULATION. Payments on cancelled invoices, payments whose invoice
 *   cannot be resolved, negative values and undated rows all stay in the list
 *   and are MARKED. Production holds three cancelled invoices still carrying
 *   payment rows worth CHF 4'000 between them; hiding them would retire a live
 *   accounting question by making it invisible.
 *
 *   THE ROW COUNT. Two payments against one invoice are two events. Production
 *   holds invoices whose two rows sum to twice the invoice total, and collapsing
 *   them by invoice id would show one plausible payment instead of two
 *   questionable ones.
 *
 * THERE IS NO TOTAL. The mirror is upsert-only and never deletes, so a sum over
 * these rows is a wrong number with a confident label. `bexio_cashflow_service`
 * owns cash-in and pays for that answer with an explicit exclusion regime.
 *
 * NO ACTION COLUMN. A payment's invoice is always a Bexio document, and this
 * repository routes no Bexio invoice detail page — `invoices/:id` is the NATIVE
 * one. A link would land somewhere else, so none is offered, exactly as
 * `InvoiceTable` decides for its own Bexio rows.
 */
import React from "react";
import { AlertTriangle, Inbox, ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import { Link } from "react-router-dom";

import BexioTablePagination from "../../../components/admin/bexio/workspace/BexioTablePagination";
import {
  BexioTableEmpty,
  BexioTableError,
  BexioTableSkeleton,
} from "../../../components/admin/bexio/workspace/BexioTableStates";
import { fmtInvoiceMoney } from "../../../components/admin/bexio/bexioFormat";
import { formatDateOnly } from "../../../utils/datetime";

/** The five columns, and which two of them R6a will order by. */
const COLUMNS = [
  { key: "invoice", label: "Rechnung" },
  { key: "party", label: "Mieter / Objekt" },
  { key: "amount", label: "Betrag", sortField: "amount", align: "text-right" },
  { key: "payment_date", label: "Zahlungsdatum", sortField: "payment_date" },
  { key: "quality", label: "Hinweise" },
];

const TH =
  "whitespace-nowrap border-b border-border bg-card px-2.5 py-2 text-left text-[10px] font-medium uppercase tracking-[0.6px] text-muted-foreground";
const TD = "px-2.5 py-2.5 align-top text-[12px] text-foreground";

const BADGE =
  "inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-medium";
const WARN =
  "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300";
const ALERT =
  "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300";

/**
 * What is doubtful about this row, in the server's terms.
 *
 * `unknown_currency` is deliberately absent: the amount cell already carries
 * "Währung unbekannt", and saying it twice per row turns the column into noise.
 * Each label DESCRIBES what the mirror proves and judges nothing — a payment row
 * against a cancelled invoice is exactly that, not a refund and not an error.
 */
const QUALITY_BADGES = [
  {
    key: "invoice_cancelled",
    label: "Rechnung storniert",
    hint: "Diese Zahlung ist einer stornierten Rechnung zugeordnet.",
    cls: WARN,
  },
  {
    key: "invoice_resolved",
    invert: true,
    label: "Rechnung nicht auffindbar",
    hint: "Zu dieser Zahlung liess sich keine Rechnung auflösen.",
    cls: WARN,
  },
  {
    key: "negative_value",
    label: "Negativbetrag",
    hint: "Der Provider hat für diese Zahlung einen negativen Betrag gemeldet.",
    cls: ALERT,
  },
];

function QualityCell({ quality }) {
  const q = quality || {};
  const shown = QUALITY_BADGES.filter((b) => (b.invert ? q[b.key] === false : q[b.key] === true));
  if (shown.length === 0) {
    return <span className="text-muted-foreground">—</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {shown.map((b) => (
        <span
          key={b.key}
          data-testid={`payment-quality-${b.key}`}
          tabIndex={0}
          title={b.hint}
          className={`${BADGE} ${b.cls} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring`}
        >
          <AlertTriangle aria-hidden="true" className="h-3 w-3" />
          {b.label}
          <span className="sr-only">{b.hint}</span>
        </span>
      ))}
    </div>
  );
}

/**
 * The invoice this payment settles.
 *
 * The document number is the label and the provider payment id is secondary —
 * an id is what a reader falls back to when there is no document, not what they
 * should read first.
 */
function InvoiceCell({ row }) {
  return (
    <div className="min-w-0">
      <div className="font-mono">
        {row.invoice?.document_nr || <span className="text-muted-foreground">—</span>}
      </div>
      {row.invoice?.title ? (
        <div className="truncate text-[11px] text-muted-foreground">{row.invoice.title}</div>
      ) : null}
      <div className="font-mono text-[11px] text-muted-foreground">
        Zahlung #{row.bexio_payment_id}
      </div>
    </div>
  );
}

/** Tenant on one line, object on the next. No id is ever shown as a label. */
function PartyCell({ row }) {
  const objectLabel = [row.unit?.label, row.room?.name].filter(Boolean).join(" · ");
  return (
    // R9: A CEILING, NOT A CUT. Swiss company tenants have names like
    // "Bernasconi-Hohenzollern von Wattenwyl Immobilien Treuhand AG", and one
    // of them used to widen this column until it dragged the whole table past
    // the viewport — and the first thing pushed out of
    // sight was the "Hinweise" column, the badges that are the only reason a
    // doubtful payment row is on screen at all. The cell is now bounded and clipped —
    // but the DATA is untouched: the full name stays in the DOM, in the
    // accessible name, and in `title` for the mouse. Nothing here shortens what
    // the row SAYS, because a silently shortened tenant name is a wrong name.
    <div
      data-testid="payment-party-cell"
      className="min-w-0 max-w-[220px]"
    >
      {row.tenant?.name ? (
        row.tenant.id ? (
          <Link
            to={`/admin/tenants/${row.tenant.id}`}
            title={row.tenant.name}
            className="block truncate text-foreground underline-offset-2 hover:underline"
          >
            {row.tenant.name}
          </Link>
        ) : (
          <span title={row.tenant.name} className="block truncate">
            {row.tenant.name}
          </span>
        )
      ) : (
        <span className="text-muted-foreground">—</span>
      )}
      {objectLabel ? (
        <div title={objectLabel} className="truncate text-[11px] text-muted-foreground">
          {objectLabel}
        </div>
      ) : null}
    </div>
  );
}

/** The day the money arrived, or an explicit refusal to name one. */
function PaymentDateCell({ row }) {
  if (!row.payment_date) {
    return (
      <span tabIndex={0} className="text-muted-foreground">
        —<span className="sr-only">Kein Zahlungsdatum erfasst</span>
      </span>
    );
  }
  return <span className="whitespace-nowrap">{formatDateOnly(row.payment_date)}</span>;
}

/**
 * The source records a payment STATUS but no payment TRANSACTIONS.
 *
 * Deliberately not an empty state: "we do not record this" and "nothing was paid"
 * are different facts, and only one of them is true here.
 */
function NotRecorded({ onShowInvoices }) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
      <Inbox aria-hidden="true" className="h-8 w-8 text-muted-foreground" />
      <p className="text-sm font-medium text-foreground">
        Für diese Rechnungen werden keine einzelnen Zahlungseingänge erfasst.
      </p>
      <p className="max-w-sm text-xs text-muted-foreground">
        In Vantio erstellte Rechnungen führen einen Zahlungsstatus, aber keine
        einzelnen Zahlungstransaktionen. Den Status findest du in der
        Rechnungsliste.
      </p>
      {onShowInvoices ? (
        <button
          type="button"
          onClick={onShowInvoices}
          className="mt-1 inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          Zu den Rechnungen
        </button>
      ) : null}
    </div>
  );
}

/** "There are none yet" — said in the terms of the mirror that fills the list. */
function EmptyPopulation() {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
      <Inbox aria-hidden="true" className="h-8 w-8 text-muted-foreground" />
      <p className="text-sm font-medium text-foreground">Noch keine Zahlungen erfasst</p>
      <p className="max-w-sm text-xs text-muted-foreground">
        Sobald eine Synchronisation gelaufen ist, erscheinen hier die
        Zahlungseingänge aus Bexio.
      </p>
    </div>
  );
}

function SortIcon({ active, direction }) {
  if (!active) return <ChevronsUpDown aria-hidden="true" className="h-3 w-3 opacity-50" />;
  return direction === "asc" ? (
    <ArrowUp aria-hidden="true" className="h-3 w-3" />
  ) : (
    <ArrowDown aria-hidden="true" className="h-3 w-3" />
  );
}

export default function PaymentsTable({
  rows = [],
  total = 0,
  availability = null,
  loading = false,
  error = "",
  filtered = false,
  onReset,
  onRetry,
  sort,
  direction,
  onSortChange,
  offset = 0,
  limit = 25,
  onOffsetChange,
  onLimitChange,
  onShowInvoices,
}) {
  const body = () => {
    // Error first, always: a failure rendered as an empty list is the most
    // reassuring possible way to be wrong.
    if (error) return <BexioTableError message={error} onRetry={onRetry} />;
    if (loading) return <BexioTableSkeleton rows={8} columns={COLUMNS.length} />;
    if (availability === "not_recorded") {
      return <NotRecorded onShowInvoices={onShowInvoices} />;
    }
    if (rows.length === 0) {
      if (filtered) {
        return <BexioTableEmpty filtered noun="Zahlungen" onReset={onReset} />;
      }
      return <EmptyPopulation />;
    }
    return (
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] border-collapse">
          <thead>
            <tr>
              {COLUMNS.map((col) => {
                const active = col.sortField && col.sortField === sort;
                return (
                  <th
                    key={col.key}
                    scope="col"
                    aria-sort={
                      col.sortField
                        ? active
                          ? direction === "asc"
                            ? "ascending"
                            : "descending"
                          : "none"
                        : undefined
                    }
                    className={`${TH} ${col.align === "text-right" ? "text-right" : ""}`}
                  >
                    {col.sortField ? (
                      <button
                        type="button"
                        onClick={() => onSortChange?.(col.sortField)}
                        className={`inline-flex items-center gap-1 rounded uppercase tracking-[0.6px] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                          active ? "text-foreground" : ""
                        }`}
                      >
                        {col.label}
                        <SortIcon active={active} direction={direction} />
                      </button>
                    ) : (
                      col.label
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.id}
                className="border-b border-border last:border-b-0 hover:bg-muted/40"
              >
                <td className={TD}>
                  <InvoiceCell row={row} />
                </td>
                <td className={TD}>
                  <PartyCell row={row} />
                </td>
                <td className={`${TD} text-right`}>
                  <div
                    className={`whitespace-nowrap text-right font-mono font-medium ${
                      row.data_quality?.negative_value
                        ? "text-rose-700 dark:text-rose-300"
                        : ""
                    }`}
                  >
                    {fmtInvoiceMoney(row.amount, row.currency)}
                  </div>
                </td>
                <td className={`${TD} whitespace-nowrap font-mono text-muted-foreground`}>
                  <PaymentDateCell row={row} />
                </td>
                <td className={TD}>
                  <QualityCell quality={row.data_quality} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      {body()}
      <BexioTablePagination
        total={total}
        offset={offset}
        limit={limit}
        onOffsetChange={onOffsetChange}
        onLimitChange={onLimitChange}
        noun="Zahlungen"
        busy={loading}
      />
    </div>
  );
}
